import { ExpandMoreRounded } from '@mui/icons-material';
import classNames from 'classnames';
import type { KeyboardEvent, ReactNode } from 'react';
import css from './FoldHeader.module.scss';

/**
 * A card header that folds the card's body while `foldable`: a button role,
 * Enter and Space, `aria-expanded`, a chevron at the end. A control inside
 * it that must not fold (a checkbox, a Retry) stops propagation on its own
 * click. Not foldable, it is a plain row without the chevron, so one markup
 * serves a card whether or not it has something to fold right now.
 */
export const FoldHeader = ({
  foldable,
  collapsed,
  onToggle,
  className,
  children,
}: {
  foldable: boolean;
  collapsed: boolean;
  onToggle: () => void;
  /** Spacing and padding: the card owns those, the fold owns the behaviour. */
  className?: string;
  children: ReactNode;
}) => {
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) {
      e.preventDefault();
      onToggle();
    }
  };
  return (
    <div
      className={classNames(css.header, foldable && css.foldable, className)}
      role={foldable ? 'button' : undefined}
      tabIndex={foldable ? 0 : undefined}
      aria-expanded={foldable ? !collapsed : undefined}
      onClick={foldable ? onToggle : undefined}
      onKeyDown={foldable ? onKeyDown : undefined}
    >
      {children}
      {foldable && (
        <ExpandMoreRounded
          className={classNames(css.chevron, collapsed && css.chevronCollapsed)}
          aria-hidden
        />
      )}
    </div>
  );
};
